import { apiBaseUrl } from './api';
import { REPORT_VERSION, ROUTE, safeHttpStatus } from './clientFailureVocabulary';

/**
 * Story 10.19 — a failed seller action reports itself to OUR OWN server.
 *
 * The #497 outage was a CORS block: the browser refused every call to tph-api,
 * so tph-api logged nothing, and the seller's failure reached no system at all
 * until a customer emailed. This module sends one small report, same-origin, to
 * `POST /api/client-failure`; the marketplace server logs it and forwards it to
 * Sentry from the SERVER SDK (D6), where the browser SDK's `ignoreErrors`, its
 * Dedupe integration and ad-blockers cannot drop it.
 *
 * 🔴 A FAILURE TO REPORT CAN NEVER BECOME A FAILURE OF THE THING BEING REPORTED
 * ON (the `papersFailureCapture.js` fence). `reportClientFailure` is called from
 * inside the catch blocks that show the seller their error; a throw from here
 * would skip that and strand them. So it returns `undefined`, never awaits,
 * never throws, and every statement is inside one `try`.
 *
 * It is called only from event handlers, thunks and the checkout gate, never
 * during render. Under SSR (no `window`) it is a no-op — and the guard keys on
 * `window`, not `navigator`: Node 21+ defines a global `navigator`.
 *
 * The body is the fixed vocabulary and nothing else (AC2.5): no listing id, no
 * user id, no URL, no message text.
 */

// AC2.3 — the tier modal fires one preview per visible tier at the same
// instant (TierUpgradeModal.js), so one outage would otherwise send one report
// per tier. The same (surface, action, code) within this window sends once.
export const DEDUPE_WINDOW_MS = 10 * 1000;

// AC2.3 — at most this many reports leave one page load, whatever happens.
export const MAX_REPORTS_PER_PAGE = 10;

let lastSentAt = new Map();
let sentThisPage = 0;

/** Test seam: forget the de-duplication window and the per-page count. */
export const __resetForTests = () => {
  lastSentAt = new Map();
  sentThisPage = 0;
};

const markerOf = error => {
  if (typeof error === 'string') return error;
  return error && typeof error.message === 'string' ? error.message : undefined;
};

/**
 * Classify a failure into the vocabulary's `code`. Accepts the error a tphApi
 * call threw, or a thunk's final code string (`'NOT_AUTHENTICATED'`, …).
 *
 * A server's own error code is NEVER passed through: every typed refusal is
 * `rejected`, and the HTTP status says which kind.
 *
 * @param {Error|string|Object} error
 * @returns {string} a code from `clientFailureVocabulary.CODE_KIND`
 */
export const classifyClientFailure = error => {
  const marker = markerOf(error);

  if (marker === 'TPH_API_UNAVAILABLE') {
    const failure = error && typeof error === 'object' ? error.failure : undefined;
    if (failure === 'network') return 'tph_api_unreachable';
    if (failure === 'http') {
      return error.httpStatus >= 500 ? 'tph_api_5xx' : 'tph_api_unexpected_status';
    }
    if (failure === 'body') return 'tph_api_unreadable_body';
    return 'tph_api_unavailable';
  }
  if (marker === 'NOT_AUTHENTICATED') return 'not_authenticated';
  if (marker === 'TOKEN_REJECTED') return 'token_rejected';

  if (
    error &&
    typeof error === 'object' &&
    (typeof error.status === 'number' || (typeof error.code === 'string' && error.code !== ''))
  ) {
    return 'rejected';
  }
  return 'unrecognised';
};

const httpStatusOf = error => {
  if (!error || typeof error !== 'object') return null;
  const raw = markerOf(error) === 'TPH_API_UNAVAILABLE' ? error.httpStatus : error.status;
  const status = safeHttpStatus(raw);
  return status === undefined ? null : status;
};

const send = (url, body) => {
  let beaconQueued = false;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      beaconQueued = navigator.sendBeacon(url, body) === true;
    }
  } catch (e) {
    beaconQueued = false;
  }
  if (beaconQueued) return;

  try {
    // `text/plain` is a CORS-safelisted type, so the dev server's cross-port
    // call needs no preflight (AC2.4); the server parses the string itself.
    const pending = window.fetch(url, {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body,
    });
    if (pending && typeof pending.catch === 'function') {
      pending.catch(() => {});
    }
  } catch (e) {
    /* best-effort: the seller's own error is already on screen */
  }
};

/**
 * Report a seller action that has finally failed (D1 — at the outcome, not at
 * the transport). Fire-and-forget.
 *
 * @param {Object} args
 * @param {string} args.surface one of clientFailureVocabulary.SURFACES
 * @param {string} args.action  one of clientFailureVocabulary.ACTIONS
 * @param {Error|string|Object} args.error what the action failed with
 * @returns {undefined}
 */
export const reportClientFailure = ({ surface, action, error } = {}) => {
  try {
    if (typeof window === 'undefined') return;

    const code = classifyClientFailure(error);
    const key = `${surface}|${action}|${code}`;
    const now = Date.now();
    const last = lastSentAt.get(key);
    if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return;
    if (sentThisPage >= MAX_REPORTS_PER_PAGE) return;
    lastSentAt.set(key, now);
    sentThisPage += 1;

    const body = JSON.stringify({
      v: REPORT_VERSION,
      surface,
      action,
      code,
      status: httpStatusOf(error),
    });
    send(`${apiBaseUrl()}${ROUTE}`, body);
  } catch (e) {
    /* best-effort: a failure to report is never a failure of the action */
  }
};
